/**
 * The one place that maps a prospect's Level between the ordinal on the wire and the words a
 * coach reads.
 *
 * ZERO IS A REAL LEVEL. `0` means High School — the most common value on a recruiting board —
 * and it is falsy in JavaScript. Every test against a player level must therefore be `!= null`,
 * never truthiness: `level ? label : "N/A"` reads a High School player as unset, and
 * `playerLevel || undefined` drops the field out of a save payload entirely.
 *
 * This module exists because four hand-rolled copies of this table were living in the prospect
 * panels — a viewer map, a save map, a display switch and a dropdown option list — and they had
 * already drifted apart on exactly that point.
 *
 * The ordinals mirror the API's `PlayerLevel` enum. Do not renumber them; they are persisted.
 */

/** The ordinal the API sends and accepts. */
export type PlayerLevel = 0 | 1 | 2;

/** JUCO's ordinal, named so that a gate on it reads as intent rather than as a magic 1. */
export const JUCO_PLAYER_LEVEL = 1 satisfies PlayerLevel;

export const PLAYER_LEVELS: readonly PlayerLevel[] = [0, 1, 2];

const PLAYER_LEVEL_LABELS: Record<PlayerLevel, string> = {
  0: "High School",
  1: "JUCO",
  2: "Transfer Portal",
};

/**
 * Labels in ordinal order.
 *
 * The label is the option list for both control primitives on this screen: `SelectPopover` is
 * string-valued and `Dropdown` carries `{ label, value }` pairs whose value is the stored form
 * string. Both round-trip through {@link playerLevelFromLabel} on save.
 */
export const PLAYER_LEVEL_LABEL_OPTIONS: readonly string[] = PLAYER_LEVELS.map(
  (level) => PLAYER_LEVEL_LABELS[level],
);

/** `{ label, value }` options for `components/Dropdown`, which stores the label as its value. */
export const PLAYER_LEVEL_DROPDOWN_OPTIONS: { label: string; value: string }[] =
  PLAYER_LEVEL_LABEL_OPTIONS.map((label) => ({ label, value: label }));

/**
 * How a level reads to a coach, or `null` when there is no level to read.
 *
 * `null` rather than a placeholder string, so each caller picks its own empty rendering — the
 * About grid says "N/A" while a form field wants "".
 */
export function formatPlayerLevel(
  playerLevel: number | null | undefined,
): string | null {
  if (playerLevel == null) return null;
  return PLAYER_LEVEL_LABELS[playerLevel as PlayerLevel] ?? null;
}

/**
 * The ordinal behind a label, or `null` when the label names no level.
 *
 * Returns `null` rather than `undefined` for an unrecognised label so a caller cannot
 * accidentally spread `playerLevel: undefined` into a payload and think it saved.
 */
export function playerLevelFromLabel(
  label: string | null | undefined,
): PlayerLevel | null {
  if (!label) return null;
  const match = PLAYER_LEVELS.find(
    (level) => PLAYER_LEVEL_LABELS[level] === label,
  );
  return match ?? null;
}

/** Whether a JC year is a meaningful thing to ask for at this level. */
export function isJucoPlayerLevel(
  playerLevel: number | null | undefined,
): boolean {
  return playerLevel === JUCO_PLAYER_LEVEL;
}
